import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Checkbox, Modal, Spin, Typography, ConfigProvider } from "antd";
import contractsService from "../../../services/contracts/contracts.service";
import { RootState, AppDispatch } from "../../../store/store";
import { closeContractGate, openContractGate } from "../../../store/slices/contract.slice";

const { Text } = Typography;

const getContractTitleTR = (contractKey: string) => {
  switch (contractKey) {
    case "MANAGER_DISCLAIMER":
      return "Sorumluluk Reddi Beyanı";
    case "MANAGER_CONSENT":
      return "Kişisel Verilerin İşlenmesine İlişkin Açık Rıza Beyanı";
    case "MANAGER_AWARENESS":
      return "Kişisel Verilerin İşlenmesine İlişkin Aydınlatma Metni";
    case "EMPLOYEE_DISCLAIMER":
      return "Sorumluluk Reddi Beyanı";
    case "EMPLOYEE_CONSENT":
      return "Kişisel Verilerin İşlenmesine İlişkin Açık Rıza Beyanı";
    case "EMPLOYEE_AWARENESS":
      return "Kişisel Verilerin İşlenmesine İlişkin Aydınlatma Metni";
    default:
      return contractKey;
  }
};

const getContractTypeLabelTR = (contractType: string | null) => {
  switch (contractType) {
    case "manager":
      return "Ara Yönetici";
    case "admin":
      return "Yönetici";
    case "employee":
      return "Çalışan";
    default:
      return "-";
  }
};

const ContractApprovalGate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isGateOpen, requiredContracts, contractType, companyName } = useSelector(
    (state: RootState) => state.contractGate
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [content, setContent] = useState<string>("");
  const [loadingContent, setLoadingContent] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [initialTotalSteps, setInitialTotalSteps] = useState(0);

  const currentContractKey = requiredContracts[currentIndex];
  const isLastStep = currentIndex === requiredContracts.length - 1;
  const stepLabel = useMemo(() => {
    const currentTotal = requiredContracts.length;
    if (!currentTotal) return "";

    const total = initialTotalSteps || currentTotal;
    const doneCount = Math.max(0, total - currentTotal);
    const currentStep = doneCount + currentIndex + 1;
    return `${currentStep}/${total}`;
  }, [currentIndex, requiredContracts.length, initialTotalSteps]);

  useEffect(() => {
    if (!isGateOpen) return;
    setInitialTotalSteps((prev) => (prev === 0 ? requiredContracts.length : prev));
    setCurrentIndex(0);
    setChecked(false);
    setContent("");
  }, [isGateOpen, requiredContracts.join("|")]);

  useEffect(() => {
    if (!isGateOpen) {
      setInitialTotalSteps(0);
    }
  }, [isGateOpen]);

  useEffect(() => {
    const fetchContent = async () => {
      if (!isGateOpen) return;
      if (!currentContractKey) return;
      try {
        setLoadingContent(true);
        const res = await contractsService.getContent(currentContractKey);
        setContent(res.content || "");
      } catch {
        setContent("");
      } finally {
        setLoadingContent(false);
      }
    };
    fetchContent();
  }, [isGateOpen, currentContractKey]);

  const handleAccept = async () => {
    if (!currentContractKey) return;
    if (!checked) return;

    try {
      setAcceptLoading(true);
      await contractsService.accept(currentContractKey);

      const status = await contractsService.getStatus();
      const remaining = status?.requiredContracts || [];

      if (remaining.length > 0) {
        dispatch(
          openContractGate({
            contractType: status.contractType ?? null,
            requiredContracts: remaining,
            contractVersion: status.contractVersion ?? null,
            companyName: status.company?.name ?? companyName ?? null,
          })
        );
      } else {
        dispatch(closeContractGate());
      }
    } catch {
      // Fail-safe: gate açık kalsın, kullanıcı retry yapabilsin.
    } finally {
      setAcceptLoading(false);
      setChecked(false);
    }
  };

  if (!isGateOpen) return null;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#7538a1",
        },
      }}
    >
      <Modal
        title="Sözleşme Onayı"
        open={true}
        footer={null}
        closable={false}
        maskClosable={false}
        width={860}
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Kapsam: {getContractTypeLabelTR(contractType)}
            {companyName ? ` | Şirket: ${companyName}` : ""}
          </Text>
          <div style={{ marginTop: 6, fontWeight: 600 }}>
            Adım: {stepLabel} - {currentContractKey ? getContractTitleTR(currentContractKey) : "-"}
          </div>
        </div>

        {loadingContent ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin />
          </div>
        ) : (
          <>
            <div
              style={{
                maxHeight: 320,
                overflow: "auto",
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
                background: "#fafafa",
              }}
            >
              {content ? (
                content.trim().startsWith("<") ? (
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                ) : (
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{content}</div>
                )
              ) : (
                <Text type="secondary">Sözleşme içeriği yüklenemedi.</Text>
              )}
            </div>

            <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)}>
              Okudum, onaylıyorum
            </Checkbox>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
              <Button
                type="primary"
                onClick={handleAccept}
                disabled={!checked || acceptLoading}
                loading={acceptLoading}
              >
                {isLastStep ? "Tamamla" : "Devam Et"}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default ContractApprovalGate;

